import React, { useState } from 'react'
import { 
  HStack, 
  Button, 
  Box, 
  Flex, 
  IconButton, 
  Menu, 
  MenuButton, 
  MenuList, 
  MenuItem,
  useBreakpointValue,
  Text
} from '@chakra-ui/react'
import { HamburgerIcon } from '@chakra-ui/icons'
import { useNavigate, useLocation } from 'react-router-dom'

export type AdminNavItem = { label: string; path: string }

type AdminNavProps = {
  items?: AdminNavItem[]
}

const DEFAULT_ITEMS: AdminNavItem[] = [
  { label: '회원 관리', path: '/admin/user' },
  { label: '커뮤니티 관리', path: '/admin/community' },
  { label: 'Q&A 관리', path: '/admin/qna' },
  { label: '공지사항 관리', path: '/admin/notice' },
  { label: '자산 관리', path: '/admin/asset' },
  { label: '경매사례 관리', path: '/admin/auction-case' },
]

const AdminNav: React.FC<AdminNavProps> = ({ items = DEFAULT_ITEMS }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  
  // 반응형 설정: md 이상에서는 가로 메뉴, 미만에서는 햄버거 메뉴
  const showHorizontalMenu = useBreakpointValue({ base: false, md: true })

  const handleMenuClick = (path: string) => {
    navigate(path)
    setIsMenuOpen(false)
  }

  const getCurrentMenuLabel = () => {
    const currentItem = items.find(item => item.path === location.pathname)
    return currentItem?.label || '관리자 메뉴'
  }

  return (
    <Box borderBottom="1px solid #e2e8f0">
      {showHorizontalMenu ? (
        // 데스크톱: 가로 메뉴
        <HStack spacing={0}>
          {items.map((menu) => {
            const active = location.pathname === menu.path
            return (
              <Button
                key={menu.path}
                variant="ghost"
                borderRadius={0}
                px={4}
                py={4}
                fontWeight="medium"
                colorScheme="gray"
                fontSize="sm"
                borderBottom={
                  active ? '3px solid #A74230' : '3px solid transparent'
                }
                onClick={() => navigate(menu.path)}
              >
                {menu.label}
              </Button>
            )
          })}
        </HStack>
      ) : (
        // 모바일: 햄버거 메뉴
        <Flex justify="space-between" align="center" px={4} py={3}>
          <Text fontSize="lg" fontWeight="semibold" color="brand.600">
            {getCurrentMenuLabel()}
          </Text>
          <Menu isOpen={isMenuOpen} onOpen={() => setIsMenuOpen(true)} onClose={() => setIsMenuOpen(false)}>
            <MenuButton
              as={IconButton}
              aria-label="관리자 메뉴"
              icon={<HamburgerIcon />}
              variant="ghost"
              size="sm"
            />
            <MenuList>
              {items.map((menu) => {
                const active = location.pathname === menu.path
                return (
                  <MenuItem
                    key={menu.path}
                    onClick={() => handleMenuClick(menu.path)}
                    bg={active ? 'brand.50' : 'white'}
                    color={active ? 'brand.600' : 'gray.700'}
                    fontWeight={active ? 'semibold' : 'normal'}
                  >
                    {menu.label}
                  </MenuItem>
                )
              })}
            </MenuList>
          </Menu>
        </Flex>
      )}
    </Box>
  )
}

export default AdminNav
